import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/base/LanguageSwitcher';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation();

  const scrollToSection = (sectionId: string) => {
    // Check if we're on the home page
    const isHomePage = window.location.pathname === '/' || window.location.pathname === __BASE_PATH__ || window.location.pathname === `${__BASE_PATH__}/`;
    
    if (isHomePage) {
      // If on home page, scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If on another page, navigate to home page first, then scroll
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    setIsMenuOpen(false);
  };

  const navigateToHome = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
    setIsMenuOpen(false);
  };

  const navigateToVehicles = () => {
    window.REACT_APP_NAVIGATE('/vehicles');
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-black/95 backdrop-blur-sm text-white sticky top-0 z-50">
      <div className="container mx-auto px-4">
        {/* Top bar */}
        <div className="flex justify-between items-center py-2 border-b border-white/10">
          <div className="flex items-center space-x-3 sm:space-x-6 text-xs sm:text-sm">
            <div className="flex items-center space-x-1 sm:space-x-2">
              <i className="ri-phone-line text-yellow-400 text-sm"></i>
              <span className="hidden sm:inline">{t('header.call')}: </span>
              <span className="text-xs sm:text-sm">+237 693 695 377</span>
            </div>
            <div className="hidden lg:flex items-center space-x-2">
              <i className="ri-map-pin-line text-yellow-400"></i>
              <span>Rhône-Poulenc, en face de l'église évangélique - Douala</span>
            </div>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <LanguageSwitcher />
            <a
              href="https://wa.me/237693695377"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-yellow-400 text-black px-2 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-semibold hover:bg-yellow-300 transition-colors whitespace-nowrap"
            >
              <span className="hidden sm:inline">{t('header.book')}</span>
              <span className="sm:hidden">Book</span>
            </a>
          </div>
        </div>

        {/* Main navigation */}
        <div className="flex justify-between items-center py-3 sm:py-4">
          <button 
            onClick={navigateToHome}
            className="flex items-center space-x-2 sm:space-x-3 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center">
              <img 
                src="https://static.readdy.ai/image/a808dc7181425b7ee1ae1c223286d53e/4f1631beadcf28a46c927c9761f1e912.png" 
                alt="NPA Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <p className="text-sm sm:text-base text-yellow-400 font-semibold">Premium Car Rental</p>
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button
              onClick={navigateToHome}
              className="hover:text-yellow-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav.home')}
            </button>
            
            <button
              onClick={navigateToVehicles}
              className="hover:text-yellow-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav.vehicles')}
            </button>

            <button
              onClick={() => scrollToSection('services')}
              className="hover:text-yellow-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="hover:text-yellow-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav.about')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="hover:text-yellow-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav.contact')}
            </button>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden text-white hover:text-yellow-400 transition-colors p-2"
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl sm:text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-white/10">
            <div className="flex flex-col space-y-4">
              <button
                onClick={navigateToHome}
                className="text-left hover:text-yellow-400 transition-colors cursor-pointer py-2"
              >
                {t('nav.home')}
              </button>
              
              <button
                onClick={navigateToVehicles}
                className="text-left hover:text-yellow-400 transition-colors cursor-pointer py-2"
              >
                {t('nav.vehicles')}
              </button>

              <button
                onClick={() => scrollToSection('services')}
                className="text-left hover:text-yellow-400 transition-colors cursor-pointer py-2"
              >
                {t('nav.services')}
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-left hover:text-yellow-400 transition-colors cursor-pointer py-2"
              >
                {t('nav.about')}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-left hover:text-yellow-400 transition-colors cursor-pointer py-2"
              >
                {t('nav.contact')}
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;